import { useState, useEffect, useRef } from "react";
import {
  ChevronDown,
  TrendingUp,
  User,
  Wallet,
  Users,
  CreditCard,
  CheckCircle,
  Menu,
  X,
  HelpCircle,
  Shield,
  FileText,
  ExternalLink,
  ShoppingCart,
  TrendingDown,
  Briefcase,
  Gauge,
  ClipboardList,
  LogOut,
  AlertCircle,
  MessageSquare,
  Globe,
} from "lucide-react";

import { paymentMethods, cryptoCurrencies } from "./data.js";
import { TrustBadge } from "./TrustBadge.jsx";
import CurrencySelector from "./CurrencySelector.jsx";
import { useQuery } from "@apollo/client/react";
import { GET_GLOBAL_PAYMENT_METHODS, GET_ACTIVE_CURRENCIES } from "../../graphql/auth.js";
import { Info } from "lucide-react";
import { subscribeToPrices } from "../../lib/priceSocket.js";
import { showErrorToast } from "@/lib/errorToast";
import { useTranslation } from "@/lib/i18n.jsx";

export const MainForm = ({
  mode,
  setMode,
  fiatAmount,
  setFiatAmount,
  cryptoAmount,
  setCryptoAmount,
  selectedCurrency,
  setSelectedCurrency,
  selectedPaymentMethod,
  setSelectedPaymentMethod,
  selectedCrypto,
  setSelectedCrypto,
  onSubmit,
  onNavigate,
  onLogout,
  isLoggedIn,
  isDark,
  userLimits
}) => {
  const { t } = useTranslation();

  const steps = [
    { id: 1, name: t('main.step.trade', 'Trade'), icon: TrendingUp },
    { id: 2, name: t('main.step.login', 'Login'), icon: User },
    { id: 3, name: t('main.step.wallet', 'Wallet'), icon: Wallet },
    { id: 4, name: t('main.step.match', 'Match'), icon: Users },
    { id: 5, name: t('main.step.pay', 'Pay'), icon: CreditCard },
    { id: 6, name: t('main.step.done', 'Done'), icon: CheckCircle },
  ];

  const [showCurrencySelector, setShowCurrencySelector] = useState(false);
  const [showCryptoSelector, setShowCryptoSelector] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeInput, setActiveInput] = useState("fiat");
  const [amountError, setAmountError] = useState("");
  const [touched, setTouched] = useState(false);
  const { data: globalMethodsData } = useQuery(GET_GLOBAL_PAYMENT_METHODS);
  const globalMethods = globalMethodsData?.getAllGlobalPaymentMethods?.data || [];

  const { data: currenciesData } = useQuery(GET_ACTIVE_CURRENCIES);
  const currencies = currenciesData?.getActiveCurrencies?.data || [];

  const paymentScrollRef = useRef(null);
  const isDraggingRef = useRef(false);
  const menuPanelRef = useRef(null);

  const displayMethods = [
    ...paymentMethods.filter((m) => m.id !== "OTHERS"),
    ...globalMethods.map((gm) => ({
      id: `GLOBAL_${gm.id}`,
      name: gm.name,
      type: "GLOBAL",
      globalMethodId: gm.id,
      isGlobal: true,
      config: gm,
      icon: { light: "/placeholder.svg", dark: "/placeholder.svg" },
    })),
  ];



  const [cryptoPrices, setCryptoPrices] = useState({});
  const [cryptoUsdPrices, setCryptoUsdPrices] = useState({}); // raw USD prices, before fiat conversion
  const [cryptoChanges, setCryptoChanges] = useState({}); // 24h % change per crypto code
  const [fiatRate, setFiatRate] = useState(1); // units of selectedCurrency per 1 USD — admin limits are stored in USD

  const CRYPTO_MAP = {
    BTC: "bitcoin",
    ETH: "ethereum",
  };

  const handleWheel = (e) => {
    if (container.scrollWidth <= container.clientWidth) return;

    const isTouchpad =
      Math.abs(e.deltaX) > 0 ||
      Math.abs(e.deltaY) < 15;

    e.preventDefault();

    const scrollAmount = isTouchpad
      ? e.deltaX || e.deltaY
      : e.deltaY;

    container.scrollBy({
      left: scrollAmount,
      behavior: "smooth",
    });
  };

  useEffect(() => {
    if (!menuOpen) return;
    const handleBlur = () => setMenuOpen(false);
    const handleMouseDown = (e) => {
      if (menuPanelRef.current && !menuPanelRef.current.contains(e.target)) {
        setMenuOpen(false);
      }
    };
    window.addEventListener("blur", handleBlur);
    document.addEventListener("mousedown", handleMouseDown);
    return () => {
      window.removeEventListener("blur", handleBlur);
      document.removeEventListener("mousedown", handleMouseDown);
    };
  }, [menuOpen]);

  useEffect(() => {
    const container = paymentScrollRef.current;

    if (!container) return;

    const handleWheel = (e) => {
      if (container.scrollWidth <= container.clientWidth) return;

      const isTouchpad =
        Math.abs(e.deltaX) > 0 ||
        Math.abs(e.deltaY) < 15;

      e.preventDefault();

      const scrollAmount = isTouchpad
        ? e.deltaX || e.deltaY
        : e.deltaY;

      container.scrollBy({
        left: scrollAmount,
        behavior: "smooth",
      });
    };

    container.addEventListener("wheel", handleWheel, {
      passive: false,
    });

    return () => {
      container.removeEventListener("wheel", handleWheel);
    };
  }, []);

  // Fiat/USD conversion rate only needs refreshing occasionally (it moves
  // nowhere near as fast as crypto prices) - fetched once per currency
  // change, separately from the fast-polling crypto price loop below so that
  // loop only ever hits crypto price APIs, not the fiat-rate one too.
  useEffect(() => {
    let cancelled = false;
    const fetchFiatRate = async () => {
      try {
        const res = await fetch(`${import.meta.env.VITE_EXCHANGERATE_API_URL}/latest/USD`);
        const data = await res.json();
        if (cancelled) return;
        setFiatRate(data?.rates?.[selectedCurrency.code] || 1);
      } catch (err) {
        console.error("Fiat rate fetch error", err);
      }
    };
    fetchFiatRate();
    return () => { cancelled = true; };
  }, [selectedCurrency]);

  // Live BTC/ETH price + 24h change via the backend's shared Binance feed
  // (src/price/price.gateway.ts) instead of each browser polling Binance's
  // REST API directly - near-real-time instead of a 5s poll lag, and one
  // upstream Binance connection shared by every user instead of one each.
  useEffect(() => {
    const unsubscribe = subscribeToPrices((prices) => {
      const btc = prices["BTCUSDT"];
      const eth = prices["ETHUSDT"];
      if (!btc || !eth) return;

      setCryptoUsdPrices({ BTC: btc.price, ETH: eth.price });
      setCryptoChanges({ BTC: btc.priceChangePercent, ETH: eth.priceChangePercent });
    });

    return unsubscribe;
  }, []);

  // Recompute fiat-converted prices whenever the USD prices or the fiat rate
  // change, instead of re-fetching anything.
  useEffect(() => {
    setCryptoPrices({
      BTC: (cryptoUsdPrices.BTC || 0) * fiatRate,
      ETH: (cryptoUsdPrices.ETH || 0) * fiatRate,
    });
  }, [cryptoUsdPrices, fiatRate]);

  const getSelectedPrice = () => {
    return cryptoPrices?.[selectedCrypto.code] || 0;
  };

  // Admin stores daily buy/sell limits in USD — display them converted into
  // whatever fiat currency the user currently has selected.
  const formatLimit = (usdValue) => {
    const converted = (usdValue || 0) * fiatRate;
    return `${selectedCurrency.symbol}${converted.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
  };

  useEffect(() => {
    if (!cryptoPrices || !getSelectedPrice()) return;

    const price = getSelectedPrice();

    if (activeInput === "crypto") {
      const crypto = parseFloat(cryptoAmount) || 0;
      setFiatAmount((crypto * price).toFixed(2));
    } else {
      const fiat = parseFloat(fiatAmount) || 0;
      setCryptoAmount((fiat / price).toFixed(8));
    }
  }, [cryptoPrices]);

  // Validation functions
  const validateAmount = (amount, min = 10, max = 50000, mode, limits) => {
    const num = parseFloat(amount);
    if (!amount || amount.trim() === "") {
      return { isValid: false, error: t('main.error.amount_required', 'Amount is required') };
    }
    if (isNaN(num)) {
      return { isValid: false, error: t('main.error.invalid_number', 'Please enter a valid number') };
    }

    // 🔥 LIMIT CHECK — admin sets limits in USD, so convert to the selected
    // fiat currency before comparing against the amount the user typed.
    if (isLoggedIn && limits) {
      if (mode === "buy" && num > (limits?.dailyBuyLimit || 0) * fiatRate) {
        return {
          isValid: false,
          error: t('main.error.buy_limit_exceeded', 'Buy limit exceeded (Max: {max})').replace('{max}', formatLimit(limits.dailyBuyLimit)),
        };
      }

      if (mode === "sell" && num > (limits?.dailySellLimit || 0) * fiatRate) {
        return {
          isValid: false,
          error: t('main.error.sell_limit_exceeded', 'Sell limit exceeded (Max: {max})').replace('{max}', formatLimit(limits.dailySellLimit)),
        };
      }
    }

    // Same basis as the max check above — admin's min values are in USD.
    // Prefer the per-mode daily min (mirrors dailyBuyLimit/dailySellLimit,
    // already auto-switched server-side for new users), fall back to the
    // flat minTradeAmount, then the plain `min` default when logged out.
    const modeMinLimit =
      mode === "buy" ? limits?.dailyBuyMinLimit : limits?.dailySellMinLimit;
    const effectiveMin =
      isLoggedIn && modeMinLimit
        ? modeMinLimit * fiatRate
        : isLoggedIn && limits?.minTradeAmount
          ? limits.minTradeAmount * fiatRate
          : min;

    if (num < effectiveMin) {
      return {
        isValid: false,
        error: t('main.error.minimum_amount', 'Minimum amount is {amount}').replace('{amount}', `${selectedCurrency.symbol}${effectiveMin.toLocaleString(undefined, { maximumFractionDigits: 2 })}`),
      };
    }
    return { isValid: true, error: "" };
  };

  const handleFiatChange = (value) => {
    setFiatAmount(value);
    setActiveInput("fiat");
    setTouched(true);

    // const validation = validateAmount(value);
    const validation = validateAmount(value, 10, 50000, mode, userLimits);
    setAmountError(validation.error);

    const fiat = parseFloat(value) || 0;
    // const calculated = fiat / selectedCrypto.price;
    const price = getSelectedPrice();
    const calculated = fiat / price;
    setCryptoAmount(calculated > 0 ? calculated.toFixed(8) : "");
  };

  const handleCryptoChange = (value) => {
    setCryptoAmount(value);
    setActiveInput("crypto");
    setTouched(true);

    const crypto = parseFloat(value) || 0;
    // const calculated = crypto * selectedCrypto.price;
    const price = getSelectedPrice();
    const calculated = crypto * price;
    setFiatAmount(calculated > 0 ? calculated.toFixed(2) : "");

    // const validation = validateAmount(calculated.toFixed(2));
    const validation = validateAmount(calculated.toFixed(2), 10, 50000, mode, userLimits);
    setAmountError(validation.error);
  };

  useEffect(() => {
    if (activeInput === "fiat" && fiatAmount) {
      const fiat = parseFloat(fiatAmount) || 0;
      // const calculated = fiat / selectedCrypto.price;
      const price = getSelectedPrice();
      const calculated = fiat / price;
      setCryptoAmount(calculated > 0 ? calculated.toFixed(8) : "");
    } else if (activeInput === "crypto" && cryptoAmount) {
      const crypto = parseFloat(cryptoAmount) || 0;
      // const calculated = crypto * selectedCrypto.price;
      const price = getSelectedPrice();
      const calculated = crypto * price;
      setFiatAmount(calculated > 0 ? calculated.toFixed(2) : "");
    }
  }, [selectedCrypto]);


  const handleSubmit = () => {
    setTouched(true);
    // const validation = validateAmount(fiatAmount);
    const validation = validateAmount(fiatAmount, 10, 50000, mode, userLimits);
    if (!validation.isValid) {
      // Only toasted here (submit attempt), not from the live-typing
      // validation in handleFiatChange/handleCryptoChange above - those run
      // on every keystroke and would spam a toast per character typed.
      setAmountError(validation.error);
      showErrorToast(validation.error);
      return;
    }
    onSubmit();
  };

  // const isFormValid = fiatAmount && parseFloat(fiatAmount) >= 10 && !amountError;
  const isFormValid =
    fiatAmount &&
    parseFloat(fiatAmount) >= 10 &&
    !amountError;

  const isDarkMode = isDark ?? (
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : true
  );


  return (
    <div className="space-y-5 p-6 flex flex-col justify-center h-full">
      {/* Step Indicator */}
      <div className="step-indicator animate-slide-up">
        {steps.map((step, index) => (
          <div key={step.id} className="step-item">
            <div className={`step-circle ${step.id === 1 ? "active" : ""}`}>
              <step.icon className="w-3.5 h-3.5" />
            </div>
            <span className={`step-label ${step.id === 1 ? "active" : ""}`}>
              {step.name}
            </span>
            {index < steps.length - 1 && <div className="step-line" />}
          </div>
        ))}
      </div>

      <div className="trade-header animate-slide-up stagger-1">
        <div className="trade-tabs-container">
          <div
            className={`trade-tab-indicator ${mode === "sell" ? "right" : "left"}`}
          />
          <button
            onClick={() => setMode("buy")}
            className={`trade-tab ${mode === "buy" ? "active" : ""}`}
          >
            {t('main.tab.buy', 'Buy')}
          </button>
          <button
            onClick={() => setMode("sell")}
            className={`trade-tab ${mode === "sell" ? "active" : ""}`}
          >
            {t('main.tab.sell', 'Sell')}
          </button>
        </div>

        <button
          className="menu-btn"
          onClick={() => setMenuOpen(true)}
          aria-label={t('main.menu.open_menu_aria', 'Open menu')}
        >
          <Menu size={18} />
        </button>
      </div>

      {menuOpen && (
        <div className="menu-overlay" onClick={() => setMenuOpen(false)}>
          <div className="menu-panel" ref={menuPanelRef} onClick={(e) => e.stopPropagation()}>
            <div className="menu-header">
              <span>{t('main.menu.title', 'Menu')}</span>
              <button onClick={() => setMenuOpen(false)}>
                <X size={18} />
              </button>
            </div>

            <ul className="menu-list">
              <li
                onClick={() => { setMode("buy"); setMenuOpen(false); }}
                className={`menu-item${mode === "buy" ? " menu-item-active" : ""}`}
              >
                <ShoppingCart size={16} />
                <span>{t('main.menu.buy_crypto', 'Buy Crypto')}</span>
              </li>
              <li
                onClick={() => { setMode("sell"); setMenuOpen(false); }}
                className={`menu-item${mode === "sell" ? " menu-item-active" : ""}`}
              >
                <TrendingDown size={16} />
                <span>{t('main.menu.sell_crypto', 'Sell Crypto')}</span>
              </li>
              <li>
                <a href={`${import.meta.env.VITE_PARENT_ORIGIN}/contact`} target="_blank" rel="noopener noreferrer" className="menu-link">
                  <HelpCircle size={16} />
                  <span>{t('main.menu.support', 'Support')}</span>
                  <ExternalLink size={14} className="menu-external" />
                </a>
              </li>
              <li>
                <a href={`${import.meta.env.VITE_PARENT_ORIGIN}/privacy`} target="_blank" rel="noopener noreferrer" className="menu-link">
                  <Shield size={16} />
                  <span>{t('main.menu.privacy_policy', 'Privacy Policy')}</span>
                  <ExternalLink size={14} className="menu-external" />
                </a>
              </li>
              <li>
                <a href={`${import.meta.env.VITE_PARENT_ORIGIN}/terms`} target="_blank" rel="noopener noreferrer" className="menu-link">
                  <FileText size={16} />
                  <span>{t('main.menu.terms_of_service', 'Terms of Service')}</span>
                  <ExternalLink size={14} className="menu-external" />
                </a>
              </li>
              <li className="menu-item">
                <a href={`${import.meta.env.VITE_PARENT_ORIGIN}/docs`} target="_blank" rel="noopener noreferrer" className="menu-link">
                  <Briefcase size={16} />
                  <span>{t('main.menu.add_widget', 'Add Widget to Your Business')}</span>
                  <ExternalLink size={14} className="menu-external" />
                </a>
              </li>
              {isLoggedIn && (
                <>
                  <li className="menu-item" onClick={() => { onNavigate('chats'); setMenuOpen(false); }}>
                    <MessageSquare size={16} />
                    <span>{t('main.menu.my_chats', 'My Chats')}</span>
                  </li>
                  <li className="menu-item">
                    <a onClick={() => { onNavigate('profile'); setMenuOpen(false); }} className="menu-item cursor-pointer">
                      <User size={16} />
                      <span>{t('main.menu.my_profile', 'My Profile')}</span>
                    </a>
                  </li>
                  <li className="menu-item">
                    <a onClick={() => { onNavigate('limits'); setMenuOpen(false); }} className="menu-item cursor-pointer">
                      <Gauge size={16} />
                      <span>{t('main.menu.my_limits', 'My Limits')}</span>
                    </a>
                  </li>
                  <li className="menu-item">
                    <a onClick={() => { onNavigate('orders'); setMenuOpen(false); }} className="menu-item cursor-pointer">
                      <ClipboardList size={16} />
                      <span>{t('main.menu.my_orders', 'My Orders')}</span>
                    </a>
                  </li>
                </>
              )}
              <li className="menu-item">
                <a href={`${import.meta.env.VITE_PARENT_ORIGIN}/merchant`} target="_blank" rel="noopener noreferrer" className="menu-link">
                  <Shield size={16} />
                  <span>{t('main.menu.register_merchant', 'Register as Merchant')}</span>
                  <ExternalLink size={14} className="menu-external" />
                </a>
              </li>
              {isLoggedIn && (
                <li
                  className="menu-item menu-logout"
                  onClick={() => {
                    setMenuOpen(false);
                    onLogout();
                  }}
                >
                  <LogOut size={16} />
                  <span>{t('main.menu.logout', 'Logout')}</span>
                </li>
              )}
            </ul>
          </div>
        </div>
      )}

      {/* You Pay Section */}
      <div className="animate-slide-up stagger-2">
        <span className="section-label">
          {mode === "buy" ? t('main.section.you_pay_fiat', 'You Pay (Fiat)') : t('main.section.you_pay_crypto', 'You Pay (Crypto)')}
        </span>
        <div className={`premium-input ${touched && amountError ? "border-destructive" : ""}`}>
          <div className="dropdown-wrapper">
            {mode === "buy" ? (
              <button
                onClick={() => setShowCurrencySelector(true)}
                className="currency-selector"
              >
                {selectedCurrency.countryCode && (
                  <img
                    src={`https://flagcdn.com/w40/${selectedCurrency.countryCode.toLowerCase()}.png`}
                    alt={selectedCurrency.code}
                    className="w-6 h-4 object-cover"
                  />
                )}
                <span className="font-bold text-sm text-foreground">{selectedCurrency.code}</span>
                <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            ) : (
              <button
                onClick={() => setShowCryptoSelector(true)}
                className="currency-selector"
              >
                <span className="text-lg font-bold text-gradient">{selectedCrypto.icon}</span>
                <span className="font-bold text-sm text-foreground">{selectedCrypto.code}</span>
                <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            )}
          </div>

          <input
            type="number"
            value={mode === "buy" ? fiatAmount : cryptoAmount}
            onChange={(e) =>
              mode === "buy"
                ? handleFiatChange(e.target.value)
                : handleCryptoChange(e.target.value)
            }
            placeholder={t('main.input.enter_amount', 'Enter amount')}
            onWheel={(e) => e.currentTarget.blur()}
            className="flex-1 px-4 py-2.5 text-xl font-bold text-right bg-transparent text-foreground placeholder:text-muted-foreground/50 focus:outline-none min-w-0"
          />
        </div>
        {touched && amountError && (
          <div className="flex items-center gap-2 mt-1 text-destructive text-sm font-medium">
            <AlertCircle className="w-4 h-4" />
            {amountError}
          </div>
        )}
        {isLoggedIn && userLimits && (
          <div className="text-xs text-muted-foreground mt-1">
            {mode === "buy"
              ? t('main.limit.buy_limit', 'Buy Limit: {min} - {max}').replace('{min}', formatLimit(userLimits.dailyBuyMinLimit || userLimits.minTradeAmount)).replace('{max}', formatLimit(userLimits.dailyBuyLimit))
              : t('main.limit.sell_limit', 'Sell Limit: {min} - {max}').replace('{min}', formatLimit(userLimits.dailySellMinLimit || userLimits.minTradeAmount)).replace('{max}', formatLimit(userLimits.dailySellLimit))}
          </div>
        )}
      </div>

      {/* Payment Methods */}
      <div className="animate-slide-up stagger-3">
        <span className="section-label">{t('main.section.payment_method', 'Payment Method')}</span>
        <div
          ref={paymentScrollRef}
          className={`
    flex gap-2 py-1 px-0.5
    overflow-x-auto overflow-y-hidden
    hide-scrollbar scroll-smooth
    snap-x snap-mandatory
    cursor-grab active:cursor-grabbing
    select-none
    ${displayMethods.length === 0
              ? "min-h-[96px] items-center"
              : "min-h-[96px]"
            }
  `}
          onMouseDown={(e) => {
            const slider = paymentScrollRef.current;

            if (!slider) return;

            isDraggingRef.current = true;

            slider.style.cursor = "grabbing";

            const startX = e.pageX - slider.offsetLeft;
            const scrollLeft = slider.scrollLeft;

            const handleMouseMove = (moveEvent) => {
              // only scroll when mouse button is pressed
              if (!isDraggingRef.current) return;

              moveEvent.preventDefault();

              const x = moveEvent.pageX - slider.offsetLeft;
              const walk = (x - startX) * 1.5;

              slider.scrollLeft = scrollLeft - walk;
            };

            const handleMouseUp = () => {
              isDraggingRef.current = false;

              slider.style.cursor = "grab";

              document.removeEventListener(
                "mousemove",
                handleMouseMove
              );

              document.removeEventListener(
                "mouseup",
                handleMouseUp
              );
            };

            document.addEventListener(
              "mousemove",
              handleMouseMove
            );

            document.addEventListener(
              "mouseup",
              handleMouseUp
            );
          }}
        >
          {displayMethods.map((method) => (
            <button
              key={method.id}
              onClick={() => setSelectedPaymentMethod(method)}
              className={`snap-start flex-shrink-0 payment-btn w-[92px] h-[82px] flex flex-col justify-center items-center gap-1.5 ${selectedPaymentMethod.id === method.id ||
                (method.isGlobal && selectedPaymentMethod.globalMethodId === method.globalMethodId)
                ? "active"
                : ""
                }`}
            >
              {method.isGlobal ? (
                <div className="flex flex-col items-center gap-1.5">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${(selectedPaymentMethod.id === method.id || selectedPaymentMethod.globalMethodId === method.globalMethodId)
                    ? "bg-primary text-white"
                    : "bg-primary/10 text-primary"
                    }`}>
                    <Globe size={16} />
                  </div>
                  <span className="text-[10px] font-bold uppercase truncate max-w-[80px]">
                    {method.name}
                  </span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-1.5">
                  <div className="h-8 flex items-center justify-center">
                    <img
                      key={`${method.id}-${isDarkMode ? "dark" : "light"}`}
                      src={
                        isDarkMode
                          ? method.icon.dark
                          : method.icon.light
                      }
                      alt={method.name}
                      className="h-full w-auto object-contain max-w-[50px]"
                    />
                  </div>
                </div>
              )}
            </button>
          ))
          }
        </div>
      </div>

      {/* You Receive Section */}
      <div className="animate-slide-up stagger-4">
        <span className="section-label">
          {mode === "buy" ? t('main.section.you_receive_crypto', 'You Receive (Crypto)') : t('main.section.you_receive_fiat', 'You Receive (Fiat)')}
        </span>
        <div className="premium-input">
          <div className="dropdown-wrapper">
            {mode === "buy" ? (
              <button
                onClick={() => setShowCryptoSelector(true)}
                className="currency-selector"
              >
                <span className="text-lg font-bold text-gradient">{selectedCrypto.icon}</span>
                <span className="font-bold text-sm text-foreground">{selectedCrypto.code}</span>
                <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            ) : (
              <button
                onClick={() => setShowCurrencySelector(true)}
                className="currency-selector"
              >
                {selectedCurrency.countryCode && (
                  <img
                    src={`https://flagcdn.com/w40/${selectedCurrency.countryCode.toLowerCase()}.png`}
                    alt={selectedCurrency.code}
                    className="w-6 h-4 object-cover"
                  />
                )}
                <span className="font-bold text-sm text-foreground">{selectedCurrency.code}</span>
                <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            )}
          </div>

          <input
            type="number"
            value={mode === "buy" ? cryptoAmount : fiatAmount}
            onChange={(e) =>
              mode === "buy"
                ? handleCryptoChange(e.target.value)
                : handleFiatChange(e.target.value)
            }
            placeholder={t('main.input.zero_placeholder', '0.00')}
            onWheel={(e) => e.currentTarget.blur()}
            className="flex-1 px-4 py-2.5 text-xl font-bold text-right bg-transparent text-foreground placeholder:text-muted-foreground/50 focus:outline-none min-w-0"
          />
        </div>
      </div>

      {/* Submit Button */}
      <div className="animate-slide-up stagger-5 pt-0.5">
        <button
          onClick={handleSubmit}
          className="btn-primary w-full"
        >
          {mode === "buy" ? t('main.submit.buy_now', 'Buy Now') : t('main.submit.sell_now', 'Sell Now')}
        </button>
      </div>

      {/* Full-screen Currency Selector */}
      <CurrencySelector
        isOpen={showCurrencySelector}
        onClose={() => setShowCurrencySelector(false)}
        onSelect={setSelectedCurrency}
        items={currencies}
        type="currency"
        selectedItem={selectedCurrency}
        cryptoPrices={cryptoPrices}
        selectedCurrency={selectedCurrency}
      />

      {/* Full-screen Crypto Selector */}
      <CurrencySelector
        isOpen={showCryptoSelector}
        onClose={() => setShowCryptoSelector(false)}
        onSelect={setSelectedCrypto}
        items={cryptoCurrencies}
        type="crypto"
        selectedItem={selectedCrypto}
        cryptoPrices={cryptoPrices}
        cryptoChanges={cryptoChanges}
        selectedCurrency={selectedCurrency}
      />
    </div>
  );
};
